/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from 'react'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {CalendarIcon, CreditCardIcon, PrinterIcon, XMarkIcon} from '@heroicons/react/24/outline'
import {toast} from 'utils/toastUtils'
import {useAppDispatch} from 'helpers/store'
import {useTypedSelector} from 'helpers/reducers'
import {closeSecondaryModal, openSecondaryModal, setHeadbarTitle} from 'helpers/actions'
import {ModalSize} from 'types/enums/ModalSize'
import BookingAction from 'store/booking/BookingAction'
import {downloadBase64File} from 'utils/exportUtils'
import ReservationCheckoutPlannerModal from 'components/ReservationCheckoutPlanner/ReservationCheckoutPlannerModal'
import BookingSellerReservationPersonsModal from './components/BookingSellerReservationPersonsModal'
import BookingLoginAction from 'store/bookingLogin/BookingLoginAction'
import UiEmptyState from 'components/Ui/UiEmptyState'
import BookingSellerRebookingModal from './components/BookingSellerRebookingModal'
import BookingSellerReservationNotesTable from './components/BookingSellerReservationNotesTable'
import ReservationPlannerModal from 'components/ReservationPlanner/ReservationPlannerModal'
import ReservationInvoiceNote from '../components/ReservationInvoiceNote'
import ReservationOfferNote from '../components/ReservationOfferNote'
import {PackageItemItemType} from 'types/enums/PackageItemItemType'
import BookingSellerJournalAllModal from './components/BookingSellerJournalAllModal'
import UiButton, {UiButtonSize, UiButtonStyle} from 'components/Ui/UiButton'
import Dates from 'utils/Dates'
import {DATE_FORMATS} from 'constants/DateFormats'
import BookingSellerPackageCartItemVouchersTable from './components/BookingSellerPackageCartItemVouchersTable'
import BookingSellerPackagesCartItemsTable from './components/BookingSellerPackagesCartItemsTable'
import {useHeadbarReminders} from 'utils/hooks/useHeadbarReminders'
import {InfoCard, InfoRow} from './components/BookingSellerReservationDetailInfo'
import BookingSellerReservationDetailRevenueSummary from './components/BookingSellerReservationDetailRevenueSummary'
import BookingSellerReservationJournal from './components/BookingSellerReservationJournal'
import {ReminderModelName} from 'types/enums/ReminderModelName'
import { capturePosthogEvent } from 'utils/posthogUtils'
import {
    getReservationAccountName,
    getReservationContactEmail,
    getReservationContactFirstName,
    getReservationContactLastName,
    getReservationContactPhone,
    getReservationTime,
} from 'utils/reservations/reservationDisplayUtils'

interface BookingSellerReservationDetailProps {
    reservationId?: number
    embedded?: boolean
    onClose?: () => void
}

const BookingSellerReservationDetail: React.FC<BookingSellerReservationDetailProps> = ({
    reservationId,
    embedded = false,
    onClose,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams<{ reservationId: string }>()
    const reservationIdParam = Number(reservationId ?? params.reservationId)

    const selectedReservation = useTypedSelector((state) =>
        (state.reservationsState?.reservations || state.seller?.reservations || []).find(
            (r) => r.id === reservationIdParam
        )
    )
    const reservationCompanyDisplayName = selectedReservation
        ? getReservationAccountName(selectedReservation)
        : ''
    const reservationFirstName = selectedReservation
        ? getReservationContactFirstName(selectedReservation)
        : ''
    const reservationLastName = selectedReservation
        ? getReservationContactLastName(selectedReservation)
        : ''
    const reservationEmail = selectedReservation
        ? getReservationContactEmail(selectedReservation)
        : '-'
    const reservationPhone = selectedReservation
        ? getReservationContactPhone(selectedReservation)
        : '-'

    useHeadbarReminders(
        !!reservationIdParam && !Number.isNaN(reservationIdParam),
        ReminderModelName.Reservation,
        reservationIdParam
    )

    const [isExportingPdf, setIsExportingPdf] = useState(false)

    const resolveDownloadableFile = (
        fileData?: string | null,
        fallbackName?: string
    ): boolean => {
        if (!fileData) return false
        const normalized = fileData.trim()
        if (!normalized) return false

        if (/^https?:\/\//i.test(normalized)) {
            window.open(normalized, '_blank', 'noopener,noreferrer')
            return true
        }

        if (/^data:application\/pdf;base64,/i.test(normalized)) {
            const base64Content = normalized.replace(/^data:application\/pdf;base64,/i, '')
            downloadBase64File(base64Content, fallbackName || 'document.pdf')
            return true
        }

        if (/^[A-Za-z0-9+/=\r\n]+$/.test(normalized)) {
            downloadBase64File(normalized.replace(/\s+/g, ''), fallbackName || 'document.pdf')
            return true
        }

        return false
    }

    const resolvedReservationTime = selectedReservation
        ? getReservationTime(selectedReservation)
        : null

    useEffect(() => {
        if (reservationIdParam && !Number.isNaN(reservationIdParam)) {
            dispatch(BookingLoginAction.fetchSellerReservationDetail(reservationIdParam))
            capturePosthogEvent('reservation_viewed', { reservation_id: reservationIdParam })
        }
    }, [dispatch, reservationIdParam])

    useEffect(() => {
        if (embedded) {
            return
        }

        const title = selectedReservation ? `${t('Reservation')} #${selectedReservation.id}` : t('Reservation detail')
        dispatch(setHeadbarTitle(title))
        return () => {
            dispatch(setHeadbarTitle(null))
        }
    }, [dispatch, embedded, t, selectedReservation])

    const onExportPdfHandler = async (mode: 'netto' | 'brutto') => {
        if (!selectedReservation) return
        setIsExportingPdf(true)
        try {
            const result = await dispatch(BookingAction.fetchReservationOffer(selectedReservation.id, mode === 'netto'))
            const exported = resolveDownloadableFile(
                result.offer,
                result.download_as || `Reservation_${selectedReservation.id}_${mode}.pdf`
            )
            if (exported) {
                toast.success(t('PDF {{mode}} exported successfully', { mode }))
            } else {
                const fallbackExported = resolveDownloadableFile(
                    selectedReservation.latest_offer_pdf,
                    `Reservation_${selectedReservation.id}_${mode}.pdf`
                )
                if (fallbackExported) {
                    toast.success(t('PDF {{mode}} exported successfully', { mode }))
                } else {
                    toast.error(t('Failed to export PDF'))
                }
            }
        } catch (error) {
            console.error('Export PDF error:', error)
            toast.error(t('Failed to export PDF'))
        } finally {
            setIsExportingPdf(false)
        }
    }

    /**
     * @deprecated Wont be used on the on reservations
     */
    const onPersonsEditHandler = async () => {
        if (!selectedReservation) return

        try {
            // The seller detail contains aggregated cart rows without their
            // packages_cart_item ids. Person assignment needs the full detail,
            // where every access ticket is represented by its own cart item.
            const response = await dispatch(
                BookingLoginAction.fetchReservationDetail(selectedReservation.id)
            )

            dispatch(
                openSecondaryModal(
                    <BookingSellerReservationPersonsModal
                        reservation={response.reservation}
                        onSuccess={() => {
                            dispatch(BookingLoginAction.fetchSellerReservationDetail(selectedReservation.id))
                        }}
                    />,
                    {
                        modalSize: ModalSize.Medium,
                        closeModal: () => dispatch(closeSecondaryModal()),
                    },
                    t('Manage Persons')
                )
            )
        } catch (error) {
            console.error('Failed to load reservation persons:', error)
            toast.error(t('Something went wrong'))
        }
    }

    /**
     * @deprecated Wont be used on the on reservations
     */
    const onRebookHandler = () => {
        if (!selectedReservation) return
        dispatch(
            openSecondaryModal(
                <BookingSellerRebookingModal
                    reservationId={selectedReservation.id}
                    currentDate={selectedReservation.date}
                    currentTime={resolvedReservationTime || '12:00'}
                    onSuccess={() => {
                        dispatch(BookingLoginAction.fetchSellerReservationDetail(selectedReservation.id))
                    }}
                />,
                {
                    modalSize: ModalSize.Small,
                },
                t('Rebook Reservation')
            )
        )
    }

    const onPlannerHandler = () => {
        if (!selectedReservation) return
        dispatch(
            openSecondaryModal(
                <ReservationPlannerModal
                    reservationId={selectedReservation.id}
                    initialPlans={
                        selectedReservation.extra?.reservation_plan || []
                    }
                    title={t('Event Zeitplan')}
                />,
                {
                    modalSize: ModalSize.Small,
                    closeModal: () => dispatch(closeSecondaryModal()),
                },
                t('Event Zeitplan')
            )
        )
    }

    const onShowAllJournalHandler = () => {
        if (!selectedReservation) return
        dispatch(
            openSecondaryModal(
                <BookingSellerJournalAllModal journals={selectedReservation.reservationJournals || []} />,
                {
                    modalSize: ModalSize.Medium,
                    closeModal: () => dispatch(closeSecondaryModal()),
                },
                t('Reservation Journal')
            )
        )
    }

    const onPaymentPlanHandler = () => {
        if (!selectedReservation) return
        dispatch(
            openSecondaryModal(
                <ReservationCheckoutPlannerModal
                    reservationId={selectedReservation.id}
                    initialPlans={selectedReservation.extra?.reservation_checkout_plan || []}
                    initialInvoiceNote={selectedReservation.extra?.invoice_note}
                    initialOfferNote={selectedReservation.extra?.offer_note}
                />,
                {
                    modalSize: ModalSize.Medium,
                    closeModal: () => dispatch(closeSecondaryModal()),
                },
                t('Payment plan')
            )
        )
    }

    if (!reservationIdParam || Number.isNaN(reservationIdParam)) {
        return <UiEmptyState title={t('Invalid Reservation ID')} />
    }

    if (!selectedReservation) {
        return (
            <div className={clsx('flex justify-center items-center h-64')}>
                <span className={clsx('text-gray-500')}>{t('Loading reservation detail...')}</span>
            </div>
        )
    }

    const packagesCart = selectedReservation.packagesCart

    return (
        <div className="BookingSellerReservationDetailWrapper h-full w-full relative overflow-hidden flex flex-col bg-white dark:bg-gray-950">
            {!embedded && (
                <button
                    type="button"
                    className={clsx(
                        'absolute right-0 top-0 z-[100] h-11 w-11 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white transition-colors'
                    )}
                    onClick={() => {
                        if (onClose) {
                            onClose()
                            return
                        }
                        const returnTo = (location.state as { returnTo?: string } | null)
                            ?.returnTo
                        navigate(
                            returnTo?.startsWith('/calendar/')
                                ? returnTo
                                : '/reservations'
                        )
                    }}
                >
                    <span className="sr-only">{t('Close')}</span>
                    <XMarkIcon className={clsx('w-5 h-5 text-white')} aria-hidden="true" />
                </button>
            )}
            <div
                className={clsx(
                    'BookingSellerReservationDetail flex flex-col bg-gray-50 dark:bg-gray-950 flex-1 overflow-y-auto'
                )}
            >
                <div className="flex-1 flex flex-col p-6 gap-6">
                <div className="BookingSellerReservationDetailGrid grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    <UiButton
                        icon={<PrinterIcon className="w-4 h-4" />}
                        title={t('Open Netto PDF')}
                        size={UiButtonSize.Small}
                        style={UiButtonStyle.Print}
                        onClick={() => onExportPdfHandler('netto')}
                        disabled={isExportingPdf}
                        className="text-[12px] h-[2.5rem]"
                    />
                    <UiButton
                        title={t('Open Brutto PDF')}
                        size={UiButtonSize.Small}
                        style={UiButtonStyle.Print}
                        icon={<PrinterIcon className="w-4 h-4" />}
                        onClick={() => onExportPdfHandler('brutto')}
                        disabled={isExportingPdf}
                        className="text-[12px] h-[2.5rem]"
                    />
                    {/*<UiButton*/}
                    {/*    title={t('Rebooking')}*/}
                    {/*    size={UiButtonSize.Small}*/}
                    {/*    style={UiButtonStyle.Secondary}*/}
                    {/*    onClick={onRebookHandler}*/}
                    {/*    className="text-[12px] h-[2.5rem]"*/}
                    {/*/>*/}
                    {/*<UiButton*/}
                    {/*    title={t('Assign seller')}*/}
                    {/*    size={UiButtonSize.Small}*/}
                    {/*    style={UiButtonStyle.Secondary}*/}
                    {/*    onClick={onAssignSellerHandler}*/}
                    {/*    className="text-[12px] h-[2.5rem]"*/}
                    {/*/>*/}
                    {/*<UiButton*/}
                    {/*    title={t('Duplicate offer')}*/}
                    {/*    size={UiButtonSize.Small}*/}
                    {/*    style={UiButtonStyle.Secondary}*/}
                    {/*    onClick={onDuplicateHandler}*/}
                    {/*    className="text-[12px] h-[2.5rem]"*/}
                    {/*/>*/}
                    <UiButton
                        title={t('Payment plan')}
                        size={UiButtonSize.Small}
                        style={UiButtonStyle.INFO}
                        onClick={onPaymentPlanHandler}
                        icon={<CreditCardIcon className="w-4 h-4" />}
                        className="text-[12px] h-[2.5rem]"
                    />
                    <UiButton
                        title={t('Event Zeitplan')}
                        size={UiButtonSize.Small}
                        style={UiButtonStyle.Primary}
                        onClick={onPlannerHandler}
                        className="text-[12px] h-[2.5rem]"
                        icon={<CalendarIcon className="w-4 h-4" />}
                    />
                </div>

                <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-12 lg:col-span-4 flex flex-col gap-6">
                        <InfoCard title={t('Reservation basic information')}>
                            <InfoRow label={t('Seller')} value={selectedReservation.seller?.name || '-'} />
                            <InfoRow
                                label={t('Check-in Code')}
                                value={
                                    <span className="font-mono text-blue-600 dark:text-blue-400 font-bold uppercase">
                                        {selectedReservation.guid?.slice(0, 8) || '-'}
                                    </span>
                                }
                            />
                            <InfoRow label={t('Package Cart ID')} value={selectedReservation.package_cart_id || '-'} />
                            <div className="border-t border-gray-100 dark:border-gray-800 my-1"></div>
                            <InfoRow
                                label={t('Event date')}
                                value={
                                    selectedReservation.date
                                        ? Dates.date(selectedReservation.date, '', DATE_FORMATS.DATE_DOT)
                                        : '–'
                                }
                                onEdit={onRebookHandler}
                            />
                            <InfoRow
                                label={t('Start')}
                                value={resolvedReservationTime ?? '–'}
                                onEdit={onRebookHandler}
                            />
                            <InfoRow
                                label={t('Reserved participants')}
                                value={selectedReservation.persons ?? '0'}
                                onEdit={onPersonsEditHandler}
                            />
                            <div className="border-t border-gray-100 dark:border-gray-800 my-1"></div>
                            <InfoRow
                                label={t('Account')}
                                value={
                                    reservationCompanyDisplayName &&
                                    reservationCompanyDisplayName !== '---'
                                        ? reservationCompanyDisplayName
                                        : '-'
                                }
                            />
                            <InfoRow
                                label={t('Email')}
                                value={reservationEmail !== '---' ? reservationEmail : '-'}
                            />
                            <InfoRow
                                label={t('First name')}
                                value={reservationFirstName || '-'}
                            />
                            <InfoRow
                                label={t('Last name')}
                                value={reservationLastName || '-'}
                            />
                            <InfoRow
                                label={t('Phone')}
                                value={reservationPhone !== '---' ? reservationPhone : '-'}
                            />
                            <div className="border-t border-gray-100 dark:border-gray-800 my-1"></div>
                        </InfoCard>

                        {packagesCart && (
                            <BookingSellerPackageCartItemVouchersTable
                                packagesCartItemVouchers={packagesCart.packagesCartItemVouchers ?? []}
                                packagesCart={packagesCart}
                                title={t('Vouchers')}
                            />
                        )}
                    </div>

                        <div className="col-span-12 lg:col-span-8 flex flex-col gap-6">
                            <BookingSellerPackagesCartItemsTable
                                packagesCartItems={
                                    packagesCart?.packagesCartItems?.filter(
                                        (i) => i.packageItem?.type !== PackageItemItemType.Upsell
                                    ) || []
                                }
                                reservation={selectedReservation}
                                title={t('Access tickets')}
                                isAccessTicket={true}
                            />

                            <BookingSellerPackagesCartItemsTable
                                packagesCartItems={
                                    packagesCart?.packagesCartItems?.filter(
                                        (i) => i.packageItem?.type === PackageItemItemType.Upsell
                                    ) || []
                                }
                                reservation={selectedReservation}
                                title={t('Upsells')}
                            />

                            <BookingSellerReservationDetailRevenueSummary
                                reservation={selectedReservation}
                                packagesCartPrice={packagesCart?.price}
                            />

                            <div className="BookingSellerReservationNotesTablesGrid grid grid-cols-1 md:grid-cols-3 gap-4">
                                <BookingSellerReservationNotesTable
                                    reservationId={selectedReservation.id}
                                    notes={selectedReservation.reservationNotes || []}
                                    className="h-[300px]"
                                />

                                <div className="flex flex-col gap-4">
                                    <ReservationInvoiceNote reservation={selectedReservation} className="h-[145px]" />
                                    <ReservationOfferNote reservation={selectedReservation} className="h-[145px]" />
                                </div>

                                <BookingSellerReservationJournal
                                    journals={selectedReservation.reservationJournals || []}
                                    onShowAll={onShowAllJournalHandler}
                                    className="h-[300px]"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default BookingSellerReservationDetail
